import React from 'react';
import type { UserProfileDetail } from '../types/userDetail.types';
import { useUserSalaries } from '../../salary/hooks/useSalary';
import { useUserLaborRates } from '../../labor-rates/hooks/useLaborRates';
import { Badge, Card } from '@/components/ui';
import { AuthMask } from '@/core/permissions/AuthMask';
import { DollarSign, Clock, FileText } from 'lucide-react';

interface Props {
  user: UserProfileDetail;
}

export function SalaryInfoTab({ user }: Props) {
  const { data: salaries, isLoading: loadingSalary } = useUserSalaries(user.id);
  const { data: laborRates, isLoading: loadingRates } = useUserLaborRates(user.id);

  if (loadingSalary || loadingRates) {
    return <div className="p-8 text-center text-slate-500 sm:text-sm text-xs">Đang tải dữ liệu...</div>;
  }

  const currentSalary = salaries?.find(s => !s.effective_to);
  const currentHourlyRate = laborRates?.find(r => r.rate_type === 'hourly' && !r.effective_to);
  const currentDailyRate = laborRates?.find(r => r.rate_type === 'daily' && !r.effective_to);

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-5 border-slate-100 shadow-sm flex items-start gap-4 bg-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-emerald-600" />
          <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <DollarSign size={20} />
          </div>
          <div>
            <p className="sm:text-sm text-xs text-slate-500 mb-1">Lương cơ bản (Tháng)</p>
            <p className="text-xl font-medium text-slate-800">
              <AuthMask module="users.salary" action="view" value={currentSalary ? `${currentSalary.base_salary.toLocaleString('vi-VN')} đ` : 'Chưa cấu hình'} />
            </p>
          </div>
        </Card>
        
        <Card className="p-5 border-slate-100 shadow-sm flex items-start gap-4 bg-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-blue-600" />
          <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Clock size={20} />
          </div>
          <div>
            <p className="sm:text-sm text-xs text-slate-500 mb-1">Đơn giá giờ (Hourly)</p>
            <p className="text-xl font-medium text-slate-800">
              <AuthMask module="users.salary" action="view" value={currentHourlyRate ? `${currentHourlyRate.amount.toLocaleString('vi-VN')} đ/h` : 'Chưa cấu hình'} />
            </p>
          </div>
        </Card>
        
        <Card className="p-5 border-slate-100 shadow-sm flex items-start gap-4 bg-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-orange-600" />
          <div className="w-10 h-10 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
            <FileText size={20} />
          </div>
          <div>
            <p className="sm:text-sm text-xs text-slate-500 mb-1">Đơn giá ngày (Daily)</p>
            <p className="text-xl font-medium text-slate-800">
              <AuthMask module="users.salary" action="view" value={currentDailyRate ? `${currentDailyRate.amount.toLocaleString('vi-VN')} đ/ngày` : 'Chưa cấu hình'} />
            </p>
          </div>
        </Card>
      </div>

      {/* Lịch sử đơn giá */}
      <div className="bg-white rounded-md border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="sm:text-sm text-xs font-medium text-slate-800">Lịch sử cấu hình đơn giá nhân công</h3>
        </div>
        <div className="p-0">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100 text-slate-500 bg-white">
                <th className="py-3 px-4 sm:text-sm text-xs font-medium">Loại đơn giá</th>
                <th className="py-3 px-4 sm:text-sm text-xs font-medium text-right">Số tiền</th>
                <th className="py-3 px-4 sm:text-sm text-xs font-medium text-center">Hiệu lực từ</th>
                <th className="py-3 px-4 sm:text-sm text-xs font-medium text-center">Đến ngày</th>
                <th className="py-3 px-4 sm:text-sm text-xs font-medium text-center">Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {(!laborRates || laborRates.length === 0) ? (
                <tr><td colSpan={5} className="py-6 text-center text-slate-500 sm:text-sm text-xs">Chưa có lịch sử cấu hình</td></tr>
              ) : (
                laborRates.map(rate => (
                  <tr key={rate.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="text-[10px] uppercase font-medium bg-slate-50 text-slate-600">
                        {rate.rate_type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right sm:text-sm text-xs font-medium text-slate-900">
                      <AuthMask module="users.salary" action="view" value={rate.amount.toLocaleString('vi-VN')} />
                    </td>
                    <td className="py-3 px-4 text-center sm:text-sm text-xs text-slate-600">
                      {new Date(rate.effective_from).toLocaleDateString('vi-VN')}
                    </td>
                    <td className="py-3 px-4 text-center sm:text-sm text-xs text-slate-600">
                      {rate.effective_to ? new Date(rate.effective_to).toLocaleDateString('vi-VN') : '---'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {!rate.effective_to ? (
                        <Badge variant="glow-success" className="text-[10px]">Đang áp dụng</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-slate-500">Đã lưu trữ</Badge>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
